<script lang="ts">
	/**
	 * One test clock: where it is, and the controls that move it forward.
	 *
	 * Shared by the providers screen and the customer page, so the two offer the same steps and
	 * word the same states. The steps are worked out from the clock's own frozen time rather than
	 * from now (`advancedBy`), and every control is off while the clock is not `ready` — the provider
	 * refuses a second advance while one is running, and a button that only ever answers with a
	 * refusal is a broken button.
	 */

	import { createEventDispatcher } from 'svelte';
	import { Badge, Button, Input } from 'flowbite-svelte';
	import type { TestClock } from '../AppTypes';
	import {
		ADVANCE_STEPS,
		advancedBy,
		clockStatusLabel,
		clockStatusTone,
		formatClockTime,
		fromLocalInputValue,
		toLocalInputValue
	} from './testClocks';

	export let clock: TestClock;
	/** A request about this clock is in flight. */
	export let busy: boolean = false;

	const dispatch = createEventDispatcher<{ advance: { frozenTime: string } }>();

	/** The custom target, prefilled a day ahead of the clock and re-seeded whenever the clock moves. */
	let custom: string = '';
	let seededFrom: string = '';
	$: if (clock.frozenTime !== seededFrom) {
		seededFrom = clock.frozenTime;
		custom = toLocalInputValue(advancedBy(clock.frozenTime, 'day'));
	}

	$: isReady = clock.status === 'ready';
	$: customTarget = fromLocalInputValue(custom);
	$: customIsForward = customTarget !== null && new Date(customTarget).getTime() > new Date(clock.frozenTime).getTime();
</script>

<div class="space-y-3">
	<div class="flex flex-wrap items-center gap-2">
		<i class="fa-solid fa-clock text-default" aria-hidden="true" />
		<span class="font-semibold tabular-nums" data-testid="clock-time">{formatClockTime(clock.frozenTime)}</span>
		<Badge color={clockStatusTone(clock.status)}>{clockStatusLabel(clock.status)}</Badge>
		{#if clock.status === 'advancing' && clock.advancingTo !== null}
			<span class="text-xs text-default">to {formatClockTime(clock.advancingTo)}</span>
		{/if}
	</div>

	<div class="flex flex-wrap items-center gap-2" role="group" aria-label="Advance the clock">
		{#each ADVANCE_STEPS as step (step.key)}
			<Button
				size="xs"
				color="alternative"
				disabled={!isReady || busy}
				on:click={() => dispatch('advance', { frozenTime: advancedBy(clock.frozenTime, step.key) })}
			>
				{step.label}
			</Button>
		{/each}
	</div>

	<form
		class="flex flex-wrap items-center gap-2"
		on:submit|preventDefault={() => customTarget !== null && dispatch('advance', { frozenTime: customTarget })}
	>
		<label class="text-xs text-default" for={`clock-target-${clock.id}`}>Advance to</label>
		<Input id={`clock-target-${clock.id}`} type="datetime-local" size="sm" class="!w-auto" bind:value={custom} />
		<Button type="submit" size="xs" color="primary" disabled={!isReady || busy || !customIsForward}>
			{#if busy}
				<i class="fa-solid fa-spinner fa-spin mr-1" />
			{:else}
				<i class="fa-solid fa-forward mr-1" />
			{/if}
			Advance
		</Button>
		{#if customTarget !== null && !customIsForward}
			<span class="text-xs text-default">A clock only moves forward.</span>
		{/if}
	</form>
</div>
